import { Box, Grid, Typography } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import GLButton from "components/v3/sharedComponents/GLButton";
import HelpModal from "./HelpModal";
import { COLORS } from "components/v3/Theme/colors";
import GradientBackground from "components/v3/layouts/GradientFramedLayout";
import CurlyArrow from "assets/images/CurlyArrow.svg";
import CheckYourEmail from "assets/images/Check-your-email.svg";
import EmailClip from "assets/images/EmailClip.png";

const RegistrationInstructions: React.FC = () => {
  const { loginWithRedirect } = useAuth0();
  const [contactModalOpen, setContactModalOpen] = useState<boolean>(false);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const possibleAccessCode: string | null = searchParams.get("accessCode");

  return (
    <GradientBackground showFooter={false}>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          height: "100%"
        }}
      >
        <HelpModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
        />
        <Grid item xs={11}>
          <Typography
            variant="h3_primary"
            sx={{
              marginBottom: "20px",
              textAlign: "center",
              marginTop: "90px"
            }}
          >
            Welcome to Genomic Life!
          </Typography>
        </Grid>
        <Grid item xs={11}>
          <Typography
            variant="body"
            sx={{
              textAlign: "center"
            }}
          >
            Before you continue, please have the following information ready:
          </Typography>
        </Grid>
        <Grid item container xs={12} justifyContent="center">
          <Grid
            item
            xs={11.5}
            sm={8}
            md={6}
            lg={5}
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "16px",
              padding: "35px 15px 10px 45px",
              margin: "40px 0",
              boxShadow: "0px 0px 11px 0px #0000000D"
            }}
          >
            <Grid item xs={11} sx={{ marginBottom: "40px" }}>
              <Grid item sx={{ marginBottom: "10px" }}>
                <Typography variant="body_strong_lg">
                  1. Look for your access code
                </Typography>
              </Grid>
              <Grid
                item
                sx={{
                  paddingLeft: "26px",
                  position: "relative",
                  "& p": {
                    lineHeight: "16.8px",
                    marginBottom: "15px"
                  }
                }}
              >
                <Typography variant="body">
                  You should have received an access code. Please check your
                  Genomic Life Welcome Email for it. The welcome email would
                  have been sent to the email address provided during
                  enrollment.
                </Typography>
                <Box
                  component="img"
                  src={EmailClip}
                  mb="10px"
                  mt="30px"
                  sx={{
                    position: "relative",
                    top: 0,
                    width: "100%"
                  }}
                />
                <Box
                  component="img"
                  src={CurlyArrow}
                  sx={{
                    width: "106px",
                    height: "71px",
                    position: "absolute",
                    right: "40px",
                    top: {
                      xs: "140px",
                      sm: "109px"
                    }
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    width: "150px",
                    background:
                      "linear-gradient(90deg, #60C2A5 0%, #2D5DA9 100%)",
                    borderRadius: "24px",
                    padding: "8px 16px",
                    marginLeft: "-10px",
                    right: {
                      xs: "-38px",
                      sm: "-110px"
                    },
                    top: {
                      xs: "111px",
                      sm: "100px"
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={CheckYourEmail}
                    sx={{
                      height: "16px",
                      top: "3px",
                      left: "10px",
                      position: "relative"
                    }}
                  />
                </Box>

                <Typography variant="body_xs_dark_gray">
                  These emails are coming from noreply@genomiclife.com.
                </Typography>
                <Typography variant="body_xs_dark_gray">
                  It is normal for some emails to not include an access code, if
                  that's what you received please click the link in the email
                  and follow the instructions.
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mb: "10px"
                  }}
                >
                  <Typography
                    sx={{
                      color: COLORS.SECONDARY,
                      marginTop: "30px"
                    }}
                  >
                    Don't have your access code?{" "}
                    <Box
                      sx={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        display: "inline"
                      }}
                      onClick={() => setContactModalOpen(true)}
                    >
                      <strong>Please let us know</strong>
                    </Box>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={11} sx={{ marginBottom: "40px" }}>
              <Grid item sx={{ marginBottom: "10px" }}>
                <Typography variant="body_strong_lg">
                  2. Keep your email ready
                </Typography>
              </Grid>
              <Grid
                item
                sx={{
                  paddingLeft: "26px",
                  "& p": {
                    lineHeight: "16.8px",
                    marginBottom: "15px"
                  }
                }}
              >
                <Typography variant="body">
                  For your security and privacy, we'll need to authenticate via
                  email.
                </Typography>
              </Grid>
              <Grid
                item
                sx={{
                  paddingLeft: "26px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "row",
                  marginTop: "30px",
                  marginBottom: "20px"
                }}
              >
                <Box
                  sx={{
                    float: "left",
                    width: "30%",
                    paddingTop: "0px",
                    marginRight: "20px",
                    height: "1px",
                    borderBottom: "1px solid",
                    borderColor: COLORS.ACCENT2
                  }}
                />
                <Typography
                  variant="body_serif_accent2"
                  sx={{
                    paddingTop: "2px",
                    textAlign: "center",
                    width: "180px"
                  }}
                >
                  Please make sure that
                </Typography>
                <Box
                  sx={{
                    float: "right",
                    width: "30%",
                    paddingTop: "0px",
                    marginLeft: "20px",
                    height: "1px",
                    borderBottom: "1px solid",
                    borderColor: COLORS.ACCENT2
                  }}
                />
              </Grid>
              <Grid item sx={{ paddingLeft: "26px" }}>
                <Typography variant="body">
                  You are enrolled in a Genomic Life membership. This was
                  offered to you by your employer at open enrollment.
                </Typography>
                {possibleAccessCode && (
                  <Typography>{possibleAccessCode}</Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        container
        spacing={2}
        justifyContent="center"
        sx={{ marginBottom: "50px" }}
      >
        <Grid item>
          <GLButton
            buttonType="primary"
            text={"Create Account"}
            onClick={() =>
              loginWithRedirect({
                authorizationParams: {
                  screen_hint: "signup"
                }
              })
            }
          />
        </Grid>
        <Grid item>
          <GLButton
            buttonType="secondary-transparent"
            text={"Log in to Existing Account"}
            onClick={() => loginWithRedirect()}
          />
        </Grid>
      </Grid>
    </GradientBackground>
  );
};

export default RegistrationInstructions;
